/**
 * useFieldGroupState (Unit 5 Phase 3) — per-site grouping for the field
 * records screen.
 *
 * THE FOURTH persisted per-user UI preference in the field zone, and a
 * deliberate SIBLING of the first three rather than a fourth storage
 * mechanism. useFieldHiddenFields owns the key (`toolsight-field-fsg-<siteId>`),
 * the schema version, the raw read and the merging write; this hook owns
 * exactly one top-level key inside that object:
 *
 *   { v: 1, hiddenFields: [...], sort: [...], filter: [...],
 *     filterLogic: 'AND' | 'OR',
 *     group: { primary: 'tool_type' | null, secondary: 'die_op' | null } }
 *
 * It imports readStored and persistPatch rather than restating them, for the
 * reason the sort and filter hooks already state: a fourth
 * localStorage.getItem/setItem pair is how one writer eventually ships a blind
 * setItem and drops the other three keys.
 *
 * SCHEMA_VERSION IS 1 AND STAYS 1. `group` is an additive sibling, not a new
 * shape. readGroup gates on the same strict `!==` readHidden, readSort and
 * readFilter do, so a bump would blank every rep's hidden columns, sort,
 * filter AND grouping in one breath.
 *
 * AN OBJECT, NOT AN ARRAY — the one structural break from sort. Sort is a list
 * because its length is the rep's decision: levels apply left to right and the
 * array IS the precedence. Grouping is capped at two NAMED slots, so the shape
 * names them. A two-element array would invite a third element that nothing
 * downstream can render.
 *
 * NO DIRECTION (D-146). A sort level carries asc/desc because the rep chose an
 * ordering; a group does not, because group order is not the rep's choice —
 * buckets collate naturally with blanks last, which is what groupRecords
 * enforces so that Sort and Group cannot disagree about where an empty cell
 * belongs.
 *
 * SECONDARY WITHOUT PRIMARY IS NOT A STATE. There is no such thing as an inner
 * grouping with no outer one, so the sanitizer collapses that pair to empty
 * rather than promoting secondary. Promotion would silently rewrite a rep's
 * stored choice into a different one.
 *
 * MEMBERSHIP IS SORTABLE_KEYS, IMPORTED. The union of every manifest across
 * every view, exactly what sort validates against — and imported rather than
 * restated. A second copy of that rule is a second thing to drift. It is
 * deliberately WIDER than the picker for the same reason it is there: the
 * picker offers the current INV/ALL view only, so flipping views must not
 * silently delete a rep's saved grouping on an ALL-only field.
 *
 * SANITIZE ON READ, NOT ONLY ON WRITE, and the value is REBUILT rather than
 * passed through — a stored object carrying extra keys from an older or
 * hand-edited build contributes only its two meaningful slots.
 *
 * NEVER THROWS — inherited whole from the storage layer. Every failure mode
 * (private browsing, quota, disabled storage, corrupt JSON, wrong version)
 * degrades to the same safe state: no grouping, this session only.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { persistPatch, readStored } from './useFieldHiddenFields';
import { SORTABLE_KEYS } from './useFieldSortState';

const SCHEMA_VERSION = 1;

/**
 * ONE frozen empty value, not a fresh object per call — the same reasoning as
 * the sort hook's EMPTY_SORT. An unchanged empty grouping keeps its identity
 * across a re-read, so React bails out of the render and the host's useMemo
 * does not re-run; and groupRecords returns its input by reference on an empty
 * grouping, so an ungrouped grid keeps the exact array it already had.
 */
const EMPTY_GROUP = Object.freeze({ primary: null, secondary: null });

/**
 * Coerce any candidate into a clean grouping. The rules, in the order applied:
 *
 *   NOT AN OBJECT (or an array, or null) → empty. An array is rejected on
 *   purpose: it is the shape a well-meaning future edit would reach for, and
 *   accepting it would put two incompatible shapes in one storage slot.
 *
 *   PRIMARY NOT A LIVE MANIFEST KEY → empty, the whole value. A grouping whose
 *   outer field no longer exists is not a grouping with one bad slot; there is
 *   nothing left to bucket by, so nothing survives.
 *
 *   SECONDARY NOT A LIVE KEY, OR EQUAL TO PRIMARY → dropped to null, primary
 *   kept. A rep who grouped by one field twice asked for one grouping; the
 *   inner pass would put every row of a bucket into a single child bucket and
 *   spend a whole level of chrome saying nothing.
 *
 * Returns the frozen EMPTY_GROUP for the empty case so identity is stable, and
 * a fresh two-key object otherwise.
 */
function sanitizeGroup(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return EMPTY_GROUP;
  const { primary, secondary } = value;
  if (typeof primary !== 'string' || !SORTABLE_KEYS.has(primary)) return EMPTY_GROUP;
  const inner = (
    typeof secondary === 'string'
    && secondary !== primary
    && SORTABLE_KEYS.has(secondary)
  ) ? secondary : null;
  return { primary, secondary: inner };
}

/**
 * How many levels of grouping are on: 0, 1 or 2.
 *
 * Exported because the View sheet's active indicator is a COUNT, not a dot —
 * it tells a rep both that a grouping is on and how much of one, which is the
 * question they actually have when they open that sheet to find out why the
 * grid looks the way it does. The host passes this straight to the panel's
 * groupCount prop.
 *
 * Defensive rather than assuming a sanitized input: a secondary with no
 * primary counts 0, matching the sanitizer's ruling that it is not a state.
 */
export function groupCount(group) {
  if (!group || typeof group !== 'object') return 0;
  if (typeof group.primary !== 'string' || group.primary === '') return 0;
  return (typeof group.secondary === 'string' && group.secondary !== '') ? 2 : 1;
}

/**
 * The grouping for a site. Empty for missing / malformed / wrong-v — mirrors
 * readHidden, readSort and readFilter arm for arm, including the strict `!==`
 * on the version. The container-type guard is kept even though the sanitizer
 * repeats it, so this reader has the same four-arm shape its siblings do.
 */
function readGroup(siteId) {
  const stored = readStored(siteId);
  if (!stored) return EMPTY_GROUP;
  if (stored.v !== SCHEMA_VERSION) return EMPTY_GROUP;
  if (!stored.group || typeof stored.group !== 'object' || Array.isArray(stored.group)) {
    return EMPTY_GROUP;
  }
  return sanitizeGroup(stored.group);
}

export default function useFieldGroupState(siteId) {
  const [group, setGroupState] = useState(() => readGroup(siteId));

  // Re-read when the rep opens a different site. Ref guard identical to every
  // sibling hook's — it keeps mount from re-reading what the lazy initialiser
  // just read, which would mint a second, equal value and force a needless
  // render.
  const loadedFor = useRef(siteId);
  useEffect(() => {
    if (loadedFor.current === siteId) return;
    loadedFor.current = siteId;
    setGroupState(readGroup(siteId));
  }, [siteId]);

  // Both mutators are inert without a site: no state change, no write. A write
  // under a literal "toolsight-field-fsg-null" key would be a leak, not a
  // fallback — the same rule every sibling hook follows.
  const setGroup = useCallback((next) => {
    if (siteId == null) return;
    const clean = sanitizeGroup(next);
    setGroupState(clean);
    persistPatch(siteId, { group: clean });
  }, [siteId]);

  const clearGroup = useCallback(() => {
    if (siteId == null) return;
    setGroupState(EMPTY_GROUP);
    persistPatch(siteId, { group: { primary: null, secondary: null } });
  }, [siteId]);

  return { group, setGroup, clearGroup };
}
